
import { UserProfile } from "@/types/profile";
import { UseFormRegister, UseFormWatch, UseFormSetValue } from "react-hook-form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useEffect } from "react";
import { TimeSelectorWithToggle } from "@/components/venue/TimeSelectorWithToggle";

interface VenueOwnerProfileFormProps {
  register: UseFormRegister<UserProfile>;
  watch: UseFormWatch<UserProfile>;
  setValue?: UseFormSetValue<UserProfile>;
}

export const VenueOwnerProfileForm = ({ register, watch, setValue }: VenueOwnerProfileFormProps) => {
  // Watch for changes in venue fields
  const venueName = watch("company_name");
  const venueWebsite = watch("website");
  const venuePhone = watch("phone_number");
  const venueLocation = watch("location");
  const venueBio = watch("bio");
  
  // Update profile display in real-time when fields change
  useEffect(() => {
    // This effect will run whenever any of the watched fields change
    console.log("Venue details updated:", {
      name: venueName,
      website: venueWebsite,
      phone: venuePhone,
      location: venueLocation,
      bio: venueBio
    });
    // The profile display in parent components will automatically update
    // since we're using react-hook-form's watch functionality
  }, [venueName, venueWebsite, venuePhone, venueLocation, venueBio]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Venue Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="company_name">Venue Name</Label>
            <Input
              id="company_name"
              {...register("company_name")}
              placeholder="Enter your venue name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="website">Website</Label>
            <Input
              id="website"
              type="url"
              {...register("website")}
              placeholder="https://yourvenue.com"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone_number">Contact Phone</Label>
            <Input
              id="phone_number"
              type="tel"
              {...register("phone_number")}
              placeholder="(555) 123-4567"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="location">Address</Label>
            <Input
              id="location"
              {...register("location")}
              placeholder="123 Main St, City, State"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="bio">Venue Description</Label>
            <Textarea
              id="bio"
              {...register("bio")}
              placeholder="Describe your venue..."
              className="min-h-[100px]"
            />
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle>Operating Hours</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
              <div key={day} className="grid grid-cols-12 gap-4 items-center">
                <Label className="col-span-3 capitalize">{day}</Label>
                <div className="col-span-9">
                  {setValue ? (
                    <TimeSelectorWithToggle
                      day={day}
                      register={register}
                      watch={watch}
                      setValue={setValue}
                    />
                  ) : (
                    <Input
                      {...register(`availability.${day}` as any)}
                      placeholder="e.g., 10:00 - 23:00 or Closed"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
